// src/Pages/InquiryDetails.jsx
import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Typography, Box, Grid, Paper, Avatar, Checkbox, Button, Divider, Snackbar, CircularProgress, FormControlLabel} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LinkIcon from "@mui/icons-material/Link";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import Chip from "@mui/material/Chip";
import { AiOutlinePlus } from "react-icons/ai";
import FloatingLabelInput from "../components/FloatingLabelInput";
import DateInput from "../components/DateInput";
import Dropdown from "../components/Dropdown";

const API = "http://192.168.1.248:3103";
const BATCH = 5;

const formatYMDToDMY = (ymd) => {
  if (!ymd) return "";
  const [year, month, day] = ymd.split("-");
  return `${day}-${month}-${year}`;
};

const avatarColors = ["#004225", "#800020", "#006D77", "#FF6F00"];

const getPriorityColor = (priority) => {
  if (!priority) return "inherit";
  const p = priority.toLowerCase();
  if (p === "high") return "red";
  if (p === "medium") return "orange";
  if (p === "low") return "green";
  return "inherit";
};

export default function InquiryDetails({ loggedInUser }) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [filters, setFilters] = useState({
    inquiryDate: "", inquiryNumber: "", status: "", priority: "",
    clientname: "", clientContact: "", mobileNo: ""
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [displayed, setDisplayed] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/status`).then(r => setStatusOptions(r.data.data || [])).catch(() => setStatusOptions([]));
    axios.get(`${API}/api/priority`).then(r => setPriorityOptions(r.data.data || [])).catch(() => setPriorityOptions([]));
    load(false, filters);
  }, []);

  const load = async (more = false, override = null) => {
    setLoading(true);
    try {
      const used = override || filters;
      const offset = more ? inquiries.length : 0;
      const resp = await axios.get(`${API}/api/inquiries`, { params: { ...used, limit: BATCH, offset, userId: loggedInUser.userId, companyId: loggedInUser.companyId, role: loggedInUser.userCategory, } });
      const recs = resp.data.records || [];
      setInquiries(more ? [...inquiries, ...recs] : recs);
      setDisplayed(more ? displayed + recs.length : recs.length);
      setTotal(resp.data.total || 0);
    } catch {
      setMessage("Error fetching inquiries.");
    } finally {
      setLoading(false);
    }
  };

  const onFilterChange = e => setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
  const onSearch = () => { load(false); setSelectedIds([]); };
  const onReset = () => {
    const empty = { inquiryDate: "", inquiryNumber: "", status: "", priority: "", clientname: "", clientContact: "", mobileNo: "" };
    setFilters(empty);
    load(false, empty);
    setSelectedIds([]);
  };
  const onAdd = () => navigate("/main/v2/inquiry", { state: { mode: "insert" } });
  const onCardClick = iq => navigate("/main/v2/inquiry", { state: { mode: "edit", inquiryData: { inquiry: iq } } });
  const onToggleAll = e => {
    if (e.target.checked) setSelectedIds(inquiries.map(i => i.inquiryId));
    else setSelectedIds([]);
  };
  const onToggleOne = (e, id) => {
    e.stopPropagation();
    if (e.target.checked) setSelectedIds(s => [...s, id]);
    else setSelectedIds(s => s.filter(x => x !== id));
  };
  const onDelete = async () => {
    if (!selectedIds.length) { setMessage("Select at least one."); return; }
    // Optional: handle followupCount validation here
    setMessage("Deleted successfully.");
    setInquiries(inquiries.filter(i => !selectedIds.includes(i.inquiryId)));
    setSelectedIds([]);
  };

  const mergedInquiries = inquiries;

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ textDecoration: "underline" }}>
        Inquiry Details
      </Typography>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}><DateInput label="Inq. Date" name="inquiryDate" value={filters.inquiryDate} onChange={onFilterChange} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><FloatingLabelInput label="Inq. No" name="inquiryNumber" value={filters.inquiryNumber} onChange={onFilterChange} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><Dropdown label="Status" name="status" options={statusOptions} value={filters.status} valueKey="LeadStatus" labelKey="LeadStatus" onChange={(opt) => onFilterChange({ target: { name: "status", value: opt.LeadStatus } })} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><Dropdown label="Priority" name="priority" options={priorityOptions} value={filters.priority} valueKey="value" labelKey="value" onChange={(opt) => onFilterChange({ target: { name: "priority", value: opt.value } })} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><FloatingLabelInput label="Client Name" name="clientname" value={filters.clientname} onChange={onFilterChange} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><FloatingLabelInput label="Contact Person" name="clientContact" value={filters.clientContact} onChange={onFilterChange} fullWidth /></Grid>
          <Grid item xs={12} sm={6} md={2}><FloatingLabelInput label="Mobile No" name="mobileNo" value={filters.mobileNo} onChange={onFilterChange} fullWidth /></Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="contained" onClick={onSearch}>Search</Button>
          <Button variant="outlined" onClick={onReset}>Reset</Button>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ mb: 2, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2 }}>
        <Button startIcon={<AiOutlinePlus />} variant="contained" onClick={onAdd}
          sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 120 }, flexShrink: 0 }}>
          Add
        </Button>
        <FormControlLabel control={
          <Checkbox checked={inquiries.length > 0 && selectedIds.length === inquiries.length}
            indeterminate={selectedIds.length > 0 && selectedIds.length < inquiries.length}
            onChange={onToggleAll} />
        } label="Select All" />
        <Typography>Count: {displayed} / {total}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button color="error" variant="contained" onClick={onDelete}>Delete</Button>
        </Box>
      </Box>

      {/* Cards */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : mergedInquiries.length === 0 ? (
        <Typography align="center" color="text.secondary" py={4}>No records found.</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            {mergedInquiries.map((inq, idx) => {
              const color = avatarColors[idx % avatarColors.length];
              const priorityColor = getPriorityColor(inq.priority);
              const pLetter = inq.priority?.charAt(0).toUpperCase() || "—";
              return (
                <Grid item xs={12} key={inq.inquiryId}>
                  <Paper elevation={2} onClick={() => onCardClick(inq)} sx={{
                    borderLeft: `6px solid ${priorityColor}`,
                    borderRadius: 1,
                    mb: 2,
                    p: { xs: 2, md: 3 },
                    bgcolor: "background.paper",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": { boxShadow: 4 },
                  }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                      <Checkbox size="small" sx={{ mr: 1 }} value={inq.inquiryId} onClick={e => e.stopPropagation()} onChange={e => onToggleOne(e, inq.inquiryId)} checked={selectedIds.includes(inq.inquiryId)} />
                      <Avatar sx={{ bgcolor: color, color: themeColors.neutral.surface, width: 45, height: 45, mr: 1.5 }}>
                        {inq.clientname?.charAt(0).toUpperCase() || "?"}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 580 }}>{inq.clientname}</Typography>
                        <Typography variant="caption" color="text.secondary">{inq.city}</Typography>
                      </Box>
                      <Chip label={`${inq.statusName} | ${pLetter}`} size="small" sx={{ bgcolor: priorityColor, color: themeColors.neutral.surface, ml: 1, opacity: 0.6 }} />
                    </Box>

                    {/* Contact + Source */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        <PersonIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                        <Typography variant="body2" noWrap>{inq.clientContact || "—"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        <LinkIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                        <Typography variant="body2" noWrap>{inq.LastleadSourceName || "—"}</Typography>
                      </Box>
                    </Box>

                    {/* Mobile + Price */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                        <Typography variant="body2" color="primary" noWrap>{inq.mobileNo || "—"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography fontSize={16} fontWeight={600} color="grey.700" sx={{ mr: 0.5 }}> ₹ </Typography>
                        <Typography variant="body2" fontWeight="600" noWrap>{inq.closingPrice ?? "0.00"}</Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {/* Footer */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AssignmentIndIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                        <Typography variant="body2" noWrap>{inq.employeeName || "—"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                        <Typography variant="body2" noWrap>{formatYMDToDMY(inq.nextFollowUpDate)}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Show More */}
          {displayed < total && (
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Button variant="contained" onClick={() => load(true)}> Show More </Button>
            </Box>
          )}
        </>
      )}

      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => setMessage("")} message={message} />
    </Container>
  );
}